import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* ──────────────────────────────────────────────────────────────────────────
   FAQ — Acordeón que resuelve las últimas objeciones antes del cierre.
   ────────────────────────────────────────────────────────────────────────── */

const FAQItem = ({ item, isOpen, onToggle, index }: { item: { q: string, a: string }, isOpen: boolean, onToggle: () => void, index: number }) => {
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;
  return (
    <div style={{ borderBottom: '1px solid var(--border-light)' }}>
      <button
        id={buttonId}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem 0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-primary)',
          textAlign: 'left',
          fontFamily: 'var(--font-body)',
          fontSize: '1.1rem',
          fontWeight: 600
        }}
      >
        <span>{item.q}</span>
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: isOpen ? 'var(--accent-purple)' : 'rgba(255,255,255,0.05)',
            color: isOpen ? '#fff' : 'var(--text-secondary)'
          }}
        >
          <Plus size={18} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ paddingBottom: '1.5rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { t } = useTranslation();

  const faqs = [
    {
      q: t('faq.q1', '¿Necesito saber de tecnología o programación?'),
      a: t('faq.a1', 'Para nada. RurayHUB se configura con clics, no con código. Si sabes usar WhatsApp, sabes usar la plataforma. Y si te atoras, nuestro equipo te acompaña en la puesta en marcha.'),
    },
    {
      q: t('faq.q2', '¿Funciona con el número de WhatsApp de mi clínica?'),
      a: t('faq.a2', 'Sí. Conectas tu número actual sin cambiarlo. Tus pacientes te siguen escribiendo al mismo lugar de siempre; la diferencia es que ahora reciben respuesta al instante.'),
    },
    {
      q: t('faq.q3', '¿Cuánto tarda en estar listo?'),
      a: t('faq.a3', 'La mayoría de clínicas quedan operativas el mismo día. Conectar WhatsApp toma minutos y la configuración inicial del agente está lista en menos de una hora.'),
    },
    {
      q: t('faq.q4', '¿Qué pasa si un paciente pregunta algo que el agente no sabe?'),
      a: t('faq.a4', 'El agente deriva la conversación a tu equipo humano sin perder el hilo. Nunca deja a un paciente sin respuesta, y tú decides en qué casos quieres intervenir.'),
    },
    {
      q: t('faq.q5', '¿Puedo cancelar cuando quiera?'),
      a: t('faq.a5', 'Claro. No hay contratos de permanencia ni penalizaciones. Te quedas porque funciona, no porque estés amarrado.'),
    },
    {
      q: t('faq.q6', '¿Tienen soporte en español?'),
      a: t('faq.a6', 'Totalmente. Somos un equipo regional: soporte en español, en tu zona horaria y que entiende cómo opera tu negocio.'),
    },
  ];

  return (
    <section id="faq" style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
      <div className="container" style={{ maxWidth: 820 }}>
        <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{
              display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--accent-purple)', marginBottom: '1.2rem',
              padding: '6px 14px', borderRadius: 999, border: '1px solid rgba(139,92,246,0.25)',
              background: 'rgba(139,92,246,0.06)',
            }}
          >
            {t('faq.eyebrow', 'Preguntas frecuentes')}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 300, color: 'var(--text-primary)', margin: 0, lineHeight: 1.12, letterSpacing: '-0.03em' }}
          >
            {t('faq.title_start', '¿Te queda alguna ')}<span className="grad-text" style={{ fontWeight: 800 }}>{t('faq.title_end', 'duda?')}</span>
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          style={{ borderTop: '1px solid var(--border-light)' }}
        >
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              index={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
